import React, { useState, useEffect, useMemo } from 'react';
import { get, request } from '../services/http-service';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import Box from '@mui/material/Box';
import DialogTitle from '@mui/material/DialogTitle';
import PropTypes from 'prop-types';
import Typography from '@mui/material/Typography';
import Loading from './Loading';
import Popper from '@mui/material/Popper';
import Fade from '@mui/material/Fade';
import Paper from '@mui/material/Paper';
import HandyManIcon from '@mui/icons-material/HandymanOutlined';
import BuildIcon from '@mui/icons-material/BuildOutlined';
import { AddOnType } from '../constants/AddOnType';
import ButtonGroup from '@mui/material/ButtonGroup';
import DialogClose from './DialogClose';
import Stack from '@mui/material/Stack';
import TextareaAutosize from '@mui/material/TextareaAutosize';

function AiButton({ documentId, documentType }) {
	const [loading, setLoading] = useState(false);
	const [message, setMessage] = useState(null);

	const [anchorEl, setAnchorEl] = useState(null);
	const [openPopper, setOpenPopper] = useState(false);

	const [currentInstructionId, setCurrentInstructionId] = useState(false);

	const [instructions, setInstructions] = useState([]);

	const [showAiButton, setShowAiButton] = useState(false);
	const [showAiDialog, setShowAiDialog] = useState(false);

	const [dialogTitle, setDialogTitle] = useState(null);

	const disclaimer = 'Notera att Ai kan göra fel!';

	const instructionLabels = {
		SUMMARY_LAW: {
			button: 'Summera lagtext',
			title: 'Ai-funktion: Summera lagtext',
		},
		REFERENCES_LAW: {
			button: 'Hämta referenser',
			title: 'Ai-funktion: Hämta referenser',
		},
	};

	const loadCurrentUser = async () => {
		let currentUserResponse = await get('/currentuser');

		if (currentUserResponse.addOnList) {
			const aiAddOn = currentUserResponse.addOnList.find(
				(addOn) => addOn.type === AddOnType.AI_FUNCTIONS
			);

			if (aiAddOn) {
				setShowAiButton(true);
			}
		}
	};

	const loadInstructions = async () => {
		let instructionResponse = await get('/ai/instructions');

		// Todo: Filter here when we have more instructions for other document types

		if (instructionResponse) {
			setInstructions(instructionResponse);
		}
	};

	useEffect(() => {
		loadCurrentUser();
	}, []);

	useEffect(() => {
		if (showAiButton) {
			loadInstructions();
		}
	}, [showAiButton]);

	const onClose = () => {
		setShowAiDialog(false);
		setMessage('');
	};

	const handlePopperClick = (event) => {
		setAnchorEl(event.currentTarget);
		setOpenPopper(!openPopper);
	};

	const sendMessage = async (intructionId, documentId, documentType) => {
		let data = {
			instructionID: intructionId,
			documentID: documentId,
			documentType: documentType,
		};

		let response = await request('POST', '/ai/send/augmented', data);

		return response;
	};

	const handleAiAction = async (
		intructionId,
		instructionLabels,
		documentId,
		documentType
	) => {
		setCurrentInstructionId(intructionId);
		setOpenPopper(false);
		setDialogTitle(instructionLabels.title);
		setShowAiDialog(true);

		setLoading(true);

		let sendMessageResponse = await sendMessage(
			intructionId,
			documentId,
			documentType
		);

		setMessage(sendMessageResponse.message);
		setLoading(false);
	};

	const onReload = async (currentInstructionId, documentId, documentType) => {
		setLoading(true);
		setMessage('');

		let sendMessageResponse = await sendMessage(
			currentInstructionId,
			documentId,
			documentType
		);

		setMessage(sendMessageResponse.message);
		setLoading(false);
	};

	return (
		<>
			{showAiButton && instructions.length > 0 && (
				<Button
					fullWidth
					size="small"
					startIcon={<HandyManIcon />}
					disableElevation
					variant="outlined"
					onClick={handlePopperClick}
				>
					Ai verktyg
				</Button>
			)}

			<Popper
				sx={{ zIndex: 9999 }}
				open={openPopper}
				anchorEl={anchorEl}
				placement={'bottom-end'}
				transition
			>
				{({ TransitionProps }) => (
					<Fade {...TransitionProps} timeout={350}>
						<Paper sx={{ marginTop: 0.2 }}>
							<ButtonGroup
								orientation="vertical"
								aria-label="Vertical button group"
								variant="outlined"
							>
								{instructions.map((instruction, index) => (
									<Button
										fullWidth
										size="small"
										startIcon={<BuildIcon />}
										disableElevation
										variant="outlined"
										key={index}
										onClick={() =>
											handleAiAction(
												instruction.id,
												instructionLabels[
													instruction.type
												],
												documentId,
												documentType
											)
										}
									>
										{
											instructionLabels[instruction.type]
												.button
										}
									</Button>
								))}
							</ButtonGroup>
						</Paper>
					</Fade>
				)}
			</Popper>

			<Dialog
				open={showAiDialog}
				onClose={onClose}
				scroll="paper"
				aria-labelledby="scroll-dialog-title"
				aria-describedby="scroll-dialog-description"
				fullWidth
				maxWidth="lg"
				sx={{
					marginBottom: '24px',
				}}
			>
				<DialogTitle id="scroll-dialog-title">
					<Box mt={0} ml={0} mr={0} mb={0}>
						{dialogTitle}
					</Box>
					<DialogClose onClose={onClose} />
				</DialogTitle>
				<DialogContent dividers>
					<Box mb={3}>
						{loading && <Loading />}
						{!loading && (
							<Box sx={{ paddingBottom: '0px' }}>
								<TextareaAutosize
									style={{
										width: '100%',
										fontSize: '14px',
										fontFamily:
											'Roboto, Helvetica, Arial, sans-serif',
										fontWeight: 400,
										color: 'rgba(0, 0, 0, 0.87)',
									}}
									aria-label="AI genererat innehåll, den summerade texten."
									placeholder=""
									readOnly={true}
									value={message}
								/>
							</Box>
						)}
					</Box>
				</DialogContent>
				<Stack
					direction={{ xs: 'column', sm: 'row' }}
					sx={{ justifyContent: 'space-between', gap: 2 }}
				>
					<Box
						sx={{
							flexShrink: 1,
							alignSelf: { xs: 'flex-start', sm: 'center' },
						}}
					>
						<Typography ml={3}>{disclaimer}</Typography>
					</Box>
					<Stack
						direction={{
							xs: 'row-reverse',
							sm: 'row',
						}}
						sx={{
							gap: 2,
							flexShrink: 0,
							alignSelf: { xs: 'flex-end', sm: 'center' },
							mt: 1,
							mb: 1,
							mr: 3,
						}}
					>
						<Button
							variant="contained"
							disabled={!(message && message.length > 0)}
							onClick={() =>
								onReload(
									currentInstructionId,
									documentId,
									documentType
								)
							}
						>
							Ladda om
						</Button>
						<Button onClick={onClose}>Avbryt</Button>
					</Stack>
				</Stack>
			</Dialog>
		</>
	);
}

AiButton.propTypes = {
	documentId: PropTypes.number.isRequired,
	documentType: PropTypes.string.isRequired,
};

export default AiButton;
